import { RoseLogo } from '@inboundx/shared/ui';
import type { ReactNode } from 'react';
import { PixelField } from './PixelField';

/**
 * Centered full-bleed pixel-field surface: an animated `PixelField` behind a
 * white-sheen overlay, the Rose wordmark, a floating icon tile, and a centered
 * message column. Shared by the post-wizard `WelcomeLoader` and the auth
 * `NoAccessScreen` (IX-4913) so the full-screen blue shell lives in one place.
 *
 * Purely visual — callers that render outside AppLayout own their browser-tab
 * title (via a ROUTE_META chromeless entry or `usePageChromeSetter`).
 */
interface PixelFieldScreenProps {
    /** Rendered inside the white tile (a status spinner, a lock, …). */
    icon: ReactNode;
    /** CSS colour the pixel field eases toward (brand-morph). Rose blue if unset. */
    tint?: string;
    /** The message column below the tile: heading, copy, and an action. */
    children: ReactNode;
}

export function PixelFieldScreen({ icon, tint, children }: PixelFieldScreenProps) {
    return (
        <div className="relative flex min-h-dvh flex-col items-center justify-center overflow-hidden bg-accent-foreground px-6">
            <PixelField tint={tint} className="absolute inset-0 size-full" />
            <div
                className="pointer-events-none absolute inset-0"
                style={{
                    backgroundImage:
                        'radial-gradient(120% 120% at 50% 0%, rgba(255,255,255,0.18), rgba(255,255,255,0) 55%)',
                }}
            />

            <div className="relative z-10 flex w-full max-w-[380px] flex-col items-center text-center">
                <RoseLogo height={26} color="var(--primary-foreground)" />
                <div className="mt-9 flex size-16 items-center justify-center rounded-2xl bg-card shadow-lg">
                    {icon}
                </div>
                {children}
            </div>
        </div>
    );
}
